import React, { useState, useRef, useEffect } from 'react';
import { Form, Col, Alert, Card } from 'react-bootstrap';
import Button from '../../components/Button';
import Fieldset from '../../components/Fieldset';
import api from '../../services/api';
import './styles_add.scss';

const Assinatura: React.FC = () => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [isDrawing, setIsDrawing] = useState(false);
	const [assinaturaSalva, setAssinaturaSalva] = useState<string | null>(null);
	const [modoEdicao, setModoEdicao] = useState(false);
	const [message, setMessage] = useState<{ type: 'success' | 'danger' | 'info'; text: string } | null>(null);
	const [loading, setLoading] = useState(false);
	const [userName, setUserName] = useState<string>('');

	useEffect(() => {
		// Obter username do localStorage ou context
		const token = localStorage.getItem('token');
		if (token) {
			try {
				const decoded: any = JSON.parse(atob(token.split('.')[1]));
				setUserName(decoded.unique_name || '');
				// Carregar assinatura existente
				carregarAssinatura(decoded.unique_name || '');
			} catch (error) {
				console.error('Erro ao decodificar token:', error);
			}
		}

		// Configurar canvas
		const canvas = canvasRef.current;
		if (canvas) {
			const ctx = canvas.getContext('2d');
			if (ctx) {
				ctx.strokeStyle = '#000';
				ctx.lineWidth = 2;
				ctx.lineCap = 'round';
			}
		}
	}, []);

	const carregarAssinatura = async (username: string) => {
		try {
			const { data } = await api.get(`/api/v1/admin/usuarios/assinatura/${username}`);
			if (data && data.assinaturaBase64) {
				setAssinaturaSalva(data.assinaturaBase64);
				// Desenhar no canvas
				const canvas = canvasRef.current;
				if (canvas) {
					const ctx = canvas.getContext('2d');
					const img = new Image();
					img.onload = () => {
						ctx?.drawImage(img, 0, 0);
					};
					img.src = data.assinaturaBase64;
				}
			}
		} catch (error: any) {
			if (error?.response?.status !== 404) {
				console.error('Erro ao carregar assinatura:', error);
			}
		}
	};

	const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const rect = canvas.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;

		const ctx = canvas.getContext('2d');
		if (ctx) {
			ctx.beginPath();
			ctx.moveTo(x, y);
			setIsDrawing(true);
		}
	};

	const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
		if (!isDrawing) return;

		const canvas = canvasRef.current;
		if (!canvas) return;

		const rect = canvas.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;

		const ctx = canvas.getContext('2d');
		if (ctx) {
			ctx.lineTo(x, y);
			ctx.stroke();
		}
	};

	const stopDrawing = () => {
		setIsDrawing(false);
	};

	// Funções para suporte a touch
	const startDrawingTouch = (e: React.TouchEvent<HTMLCanvasElement>) => {
		e.preventDefault();
		const canvas = canvasRef.current;
		if (!canvas) return;

		const rect = canvas.getBoundingClientRect();
		const touch = e.touches[0];
		const x = touch.clientX - rect.left;
		const y = touch.clientY - rect.top;

		const ctx = canvas.getContext('2d');
		if (ctx) {
			ctx.beginPath();
			ctx.moveTo(x, y);
			setIsDrawing(true);
		}
	};

	const drawTouch = (e: React.TouchEvent<HTMLCanvasElement>) => {
		e.preventDefault();
		if (!isDrawing) return;

		const canvas = canvasRef.current;
		if (!canvas) return;

		const rect = canvas.getBoundingClientRect();
		const touch = e.touches[0];
		const x = touch.clientX - rect.left;
		const y = touch.clientY - rect.top;

		const ctx = canvas.getContext('2d');
		if (ctx) {
			ctx.lineTo(x, y);
			ctx.stroke();
		}
	};

	const stopDrawingTouch = (e: React.TouchEvent<HTMLCanvasElement>) => {
		e.preventDefault();
		setIsDrawing(false);
	};

	const limparCanvas = () => {
		const canvas = canvasRef.current;
		if (canvas) {
			const ctx = canvas.getContext('2d');
			if (ctx) {
				ctx.clearRect(0, 0, canvas.width, canvas.height);
			}
		}
		setMessage(null);
	};

	const iniciarEdicao = () => {
		setModoEdicao(true);
		setMessage(null);
		// Limpar canvas ao entrar em modo de edição
		setTimeout(() => {
			limparCanvas();
		}, 100);
	};

	const cancelarEdicao = () => {
		setModoEdicao(false);
		setMessage(null);
		limparCanvas();
		// Se já tinha assinatura, redesenhar no canvas
		if (assinaturaSalva) {
			const canvas = canvasRef.current;
			if (canvas) {
				const ctx = canvas.getContext('2d');
				const img = new Image();
				img.onload = () => {
					ctx?.drawImage(img, 0, 0);
				};
				img.src = assinaturaSalva;
			}
		}
	};

	const salvarAssinatura = async () => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		// Verificar se o canvas está vazio
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
		const isEmpty = !imageData.data.some(channel => channel !== 0);

		if (isEmpty) {
			setMessage({ type: 'danger', text: 'Por favor, desenhe sua assinatura antes de salvar.' });
			return;
		}

		setLoading(true);
		setMessage(null);

		try {
			const base64String = canvas.toDataURL('image/png');
			
			await api.post('/api/v1/admin/usuarios/assinatura', {
				userName: userName,
				assinaturaBase64: base64String
			});

			setAssinaturaSalva(base64String);
			setModoEdicao(false);
			setMessage({ type: 'success', text: 'Assinatura salva com sucesso!' });
		} catch (error: any) {
			console.error('Erro ao salvar assinatura:', error);
			setMessage({ 
				type: 'danger', 
				text: error?.response?.data?.message || 'Erro ao salvar assinatura. Tente novamente.' 
			});
		} finally {
			setLoading(false);
		}
	};

	const uploadImagem = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		// Validar tipo de arquivo
		if (!file.type.startsWith('image/')) {
			setMessage({ type: 'danger', text: 'Por favor, selecione um arquivo de imagem válido.' });
			return;
		}

		// Validar tamanho (max 5MB)
		if (file.size > 5 * 1024 * 1024) {
			setMessage({ type: 'danger', text: 'A imagem deve ter no máximo 5MB.' });
			return;
		}

		const reader = new FileReader();
		reader.onload = (event) => {
			const img = new Image();
			img.onload = () => {
				const canvas = canvasRef.current;
				if (canvas) {
					const ctx = canvas.getContext('2d');
					if (ctx) {
						// Limpar canvas
						ctx.clearRect(0, 0, canvas.width, canvas.height);
						
						// Calcular dimensões mantendo aspecto
						const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
						const x = (canvas.width - img.width * scale) / 2;
						const y = (canvas.height - img.height * scale) / 2;
						
						ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
						setMessage({ type: 'info', text: 'Imagem carregada. Clique em "Salvar Assinatura" para confirmar.' });
					}
				}
			};
			img.src = event.target?.result as string;
		};
		reader.readAsDataURL(file);
	};

	return (
		<div className="container-fluid">
			<div className="row">
				<div className="col-12">
					<h3>Gerenciar Assinatura</h3>
					<p className="text-muted">
						{assinaturaSalva && !modoEdicao
							? 'Sua assinatura será utilizada nos documentos do Caderno LPMA.'
							: 'Desenhe sua assinatura usando o mouse/touch ou faça upload de uma imagem.'}
					</p>
				</div>
			</div>

			{message && (
				<div className="row mt-3">
					<div className="col-12">
						<Alert variant={message.type} onClose={() => setMessage(null)} dismissible>
							{message.text}
						</Alert>
					</div>
				</div>
			)}

			{/* MODO VISUALIZAÇÃO - Mostra assinatura salva */}
			{assinaturaSalva && !modoEdicao ? (
				<Fieldset title="Sua Assinatura">
					<div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
						<Card style={{ maxWidth: '800px', width: '100%' }}>
							<Card.Body style={{ 
								padding: '30px',
								backgroundColor: '#f8f9fa',
								textAlign: 'center',
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center'
							}}>
								<img 
									src={assinaturaSalva} 
									alt="Sua assinatura" 
									style={{ 
										maxWidth: '100%', 
										height: 'auto',
										border: '2px solid #dee2e6',
										borderRadius: '4px',
										padding: '20px',
										backgroundColor: '#fff',
										display: 'block'
									}}
								/>
							</Card.Body>
						</Card>
					</div>

					<div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
						<Button 
							title="Editar Assinatura" 
							contained 
							type="button" 
							onClick={iniciarEdicao}
						/>
					</div>
				</Fieldset>
			) : (
				/* MODO EDIÇÃO - Canvas e upload */
				<Fieldset title={assinaturaSalva ? "Editar Assinatura" : "Criar Assinatura"}>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label><strong>1. Desenhe sua assinatura no campo abaixo:</strong></Form.Label>
							<div style={{ 
								border: '2px dashed #6c757d', 
								borderRadius: '8px', 
								backgroundColor: '#fff',
								marginBottom: '20px',
								padding: '10px'
							}}>
							<canvas
								ref={canvasRef}
								width={800}
								height={200}
								onMouseDown={startDrawing}
								onMouseMove={draw}
								onMouseUp={stopDrawing}
								onMouseLeave={stopDrawing}
								onTouchStart={startDrawingTouch}
								onTouchMove={drawTouch}
								onTouchEnd={stopDrawingTouch}
								style={{ 
									display: 'block', 
									cursor: 'crosshair',
									width: '100%',
									maxWidth: '800px',
									height: 'auto',
									touchAction: 'none'
								}}
							/>
							</div>
							<Form.Text className="text-muted">
								<i className="fas fa-info-circle"></i> Use o mouse ou toque na tela para desenhar sua assinatura
							</Form.Text>
						</Form.Group>
					</Form.Row>

					<div style={{ 
						textAlign: 'center', 
						margin: '20px 0',
						color: '#6c757d',
						fontWeight: 'bold'
					}}>
						- OU -
					</div>

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label><strong>2. Faça upload de uma imagem:</strong></Form.Label>
							<Form.Control
								type="file"
								accept="image/*"
								onChange={uploadImagem}
								style={{ maxWidth: '500px' }}
							/>
							<Form.Text className="text-muted">
								<i className="fas fa-upload"></i> Formatos aceitos: JPG, PNG, GIF (máx. 5MB)
							</Form.Text>
						</Form.Group>
					</Form.Row>

					<hr className="my-4" />

					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<div className="button-position">
								{assinaturaSalva && (
									<Button 
										title="Cancelar" 
										contained={false} 
										type="button" 
										onClick={cancelarEdicao}
									/>
								)}
								<Button 
									title="Limpar" 
									contained={false} 
									type="button" 
									onClick={limparCanvas}
								/>
								<Button 
									title={loading ? "Salvando..." : "Salvar Assinatura"} 
									contained 
									type="button" 
									onClick={salvarAssinatura}
									disabled={loading}
								/>
							</div>
						</Form.Group>
					</Form.Row>
				</Fieldset>
			)}
		</div>
	);
};

export default Assinatura;

